import { Fields, Form2 } from "@cd2/forms/build/es6"
import React from "react"
import { useAsyncEffect } from "../../hooks/useAsyncEffect"
import { IRecord } from "../../types/schema"
import { IBranchSettings } from "../../utils/useSettings"
import { IWebClient } from "@cd2/skeem-client"

type IProduct = IRecord<"products", "body" | "sale_price_pennies">

export function DpdProductSelectList({
  label,
  onChange,
  value,
  isRequired,
  error,
  branchId,
  filter,
  orderRequestFilter,
}: {
  label?: React.ReactNode
  onChange: (record: IProduct | null) => void
  value?: IProduct
  isRequired?: boolean
  error?: string
  branchId: string
  filter?: any
  orderRequestFilter?: boolean
}) {
  const orderRequestFilterNames = [
    "Collection",
    "FEDEX - By 09:00AM",
    "FEDEX - By 10:00AM",
    "FEDEX - By 12:00PM",
    "FEDEX - Free Shipping",
    "FEDEX - Saturday",
    "DPD - ",
  ]
  const skeem = window.skeem
  const [products, setProducts] = React.useState<IProduct[]>([])

  useAsyncEffect(async () => {
    const branch = await skeem.fetch2<IRecord<"branches"> & { settings?: IBranchSettings }>({
      first: {
        name: "branches",
        attributes: ["settings"],
        filter: { matches: { id: branchId } },
      },
    })
    await loadOptions(branch.settings?.dpdProductPrefixes ?? "")
  }, [])

  async function loadOptions(prefixes?: string) {
    if (!prefixes?.trim()) {
      return Promise.resolve([])
    }

    const dpdProductPrefixArrayTrimmed =
      prefixes
        ?.split(",")
        .map(el => el.trim())
        .filter(Boolean) ?? []

    if (dpdProductPrefixArrayTrimmed.length === 0) {
      return Promise.resolve([])
    }

    const products = await skeem.fetch2<IProduct[]>({
      model: {
        name: "products",
        attributes: ["body", "sale_price_pennies"],
        filter: {
          and: [
            {
              or: dpdProductPrefixArrayTrimmed.map(prefix => ({
                matches: { body: { like: prefix } },
              })),
            },
            { matches: { branch: branchId } },
            filter,
            orderRequestFilter && {
              or: orderRequestFilterNames.map(v => ({ matches: { body: { like: v } } })),
            },
          ].filter(Boolean),
        },
        sort: { by: "body", direction: "asc" },
      },
    })
    setProducts(products)
  }

  return (
    <Fields.Select2
      label={<Form2.FieldLabel fieldName="dpd_select" label={label} required={isRequired} />}
      options={products}
      onChange={product => onChange(product ?? null)}
      value={value}
      getOptionValue={opt => opt.id}
      getOptionLabel={opt => opt.body}
      error={error}
      isClearable
    />
  )
}

export async function loadDpdOption(skeem: IWebClient, name: string, branchId: string) {
  const branch = await skeem.fetch2<IRecord<"branches"> & { settings: IBranchSettings }>({
    first: { name: "branches", attributes: ["settings"], matches: { id: branchId } },
  })
  const prefixes = branch?.settings?.dpdProductPrefixes ?? ""
  if (!prefixes?.trim()) {
    console.log("no prefix")
    return Promise.resolve(null)
  }

  const dpdProductPrefixArrayTrimmed =
    prefixes
      ?.split(",")
      .map(el => el.trim())
      .filter(Boolean) ?? []

  if (dpdProductPrefixArrayTrimmed.length === 0) {
    console.log("no prefix")
    return Promise.resolve(null)
  }

  const product = await skeem.fetch2<IProduct>({
    first: {
      name: "products",
      attributes: ["body", "sale_price_pennies"],
      filter: {
        and: [
          {
            or: dpdProductPrefixArrayTrimmed.map(prefix => ({
              matches: { body: { like: prefix } },
            })),
          },
          { matches: { branch: branch.id } },
          { matches: { body: { like: name } } },
        ].filter(Boolean),
      },
      sort: { by: "body", direction: "asc" },
    },
  })
  return product
}
